import api from './api';

// Utility function to fetch settings from backend API
export const fetchSettingsFromAPI = async () => {
  try {
    // Fetch general settings and company settings in parallel
    const [settingsResponse, companyResponse] = await Promise.allSettled([
      api.get('/admin/settings'),
      api.get('/admin/company-settings')
    ]);

    // Process general settings
    let fetchedSettings = {};
    if (settingsResponse.status === 'fulfilled') {
      fetchedSettings = settingsResponse.value.data || {};
      console.log('✅ General settings loaded');
    } else {
      console.warn('⚠️ General settings API failed:', settingsResponse.reason?.message);
      // Use default settings
      fetchedSettings = {
        invoiceSettings: {
          prefix: 'INV-',
          startingNumber: 1201,
          taxRate: 18,
          discountRate: 10,
          currentNumber: 1201
        },
        estimateSettings: {
          prefix: 'EST-',
          startingNumber: 1001,
          validityDays: 30,
          currentNumber: 1001
        },
        closedOrderSettings: {
          prefix: 'CO-',
          startingNumber: 2001,
          currentNumber: 2001
        },
        systemSettings: {
          currency: 'INR',
          dateFormat: 'DD/MM/YYYY',
          backupFrequency: 'daily'
        }
      };
    }

    // Process company settings
    let companySettings = {};
    if (companyResponse.status === 'fulfilled') {
      companySettings = companyResponse.value.data || {};
      console.log('✅ Company settings loaded');
    } else {
      console.warn('⚠️ Company settings API failed:', companyResponse.reason?.message);
      // Use default company settings
      companySettings = {
        companyName: 'IDEAL TRENDZ INTERIORS',
        addressLine1: '# Vertex Privilege, Villa No. 32, Nizampet Road',
        city: 'HYDERNAGAR',
        state: 'TELANGANA',
        pincode: '500072',
        contactNumber: '9014814901',
        emailId: 'idealtrendz.int@gmail.com',
        gstNumber: '36AHZPN3524A1Z9',
        panNumber: 'AHZPN3524A',
        bankName: 'HDFC Bank',
        accountHolderName: 'IDEAL TRENDZ INTERIORS',
        accountNumber: '50200012345678',
        ifscCode: 'HDFC0001234',
        branchName: 'Nizampet'
      };
    }

    // Merge all settings - ensure company settings override general settings for document numbering
    const mergedSettings = {
      ...fetchedSettings,
      companyDetails: {
        companyName: companySettings.companyName ?? 'IDEAL TRENDZ INTERIORS',
        addressLine1: companySettings.addressLine1 ?? '# Vertex Privilege, Villa No. 32, Nizampet Road',
        addressLine2: companySettings.addressLine2 ?? '',
        city: companySettings.city ?? 'HYDERNAGAR',
        state: companySettings.state ?? 'TELANGANA',
        pincode: companySettings.pincode ?? '500072',
        contactNumber: companySettings.contactNumber ?? '9014814901',
        emailId: companySettings.emailId ?? 'idealtrendz.int@gmail.com',
        gstNumber: companySettings.gstNumber ?? '36AHZPN3524A1Z9',
        panNumber: companySettings.panNumber ?? 'AHZPN3524A',
        bankName: companySettings.bankName ?? 'HDFC Bank',
        accountHolderName: companySettings.accountHolderName ?? 'IDEAL TRENDZ INTERIORS',
        accountNumber: companySettings.accountNumber ?? '50200012345678',
        ifscCode: companySettings.ifscCode ?? 'HDFC0001234',
        branchName: companySettings.branchName ?? 'Nizampet'
      },
      // Override document numbering settings with company settings if available
      estimateSettings: {
        ...fetchedSettings.estimateSettings,
        prefix: companySettings.estimatePrefix || fetchedSettings.estimateSettings?.prefix || 'EST-',
        startingNumber: companySettings.estimateStartingNumber || fetchedSettings.estimateSettings?.startingNumber || 1001,
        currentNumber: companySettings.estimateStartingNumber || fetchedSettings.estimateSettings?.currentNumber || 1001
      },
      invoiceSettings: {
        ...fetchedSettings.invoiceSettings,
        prefix: companySettings.invoicePrefix || fetchedSettings.invoiceSettings?.prefix || 'INV-',
        startingNumber: companySettings.invoiceStartingNumber || fetchedSettings.invoiceSettings?.startingNumber || 1201,
        currentNumber: companySettings.invoiceStartingNumber || fetchedSettings.invoiceSettings?.currentNumber || 1201
      },
      closedOrderSettings: {
        prefix: companySettings.closedOrderPrefix || fetchedSettings.closedOrderSettings?.prefix || 'CO-',
        startingNumber: companySettings.closedOrderStartingNumber || fetchedSettings.closedOrderSettings?.startingNumber || 2001,
        currentNumber: companySettings.closedOrderStartingNumber || fetchedSettings.closedOrderSettings?.currentNumber || 2001
      },
      paymentsSettings: {
        overduePeriodDays: companySettings.overduePeriodDays || 30
      }
    };

    console.log('✅ All settings merged successfully');
    return mergedSettings;

  } catch (error) {
    console.error('❌ Failed to fetch settings:', error);
    throw error;
  }
};

// Helper function to get specific setting values
export const getEstimateSettings = async () => {
  try {
    const settings = await fetchSettingsFromAPI();
    return settings.estimateSettings;
  } catch (error) {
    console.warn('Using default estimate settings due to API error');
    return {
      prefix: 'EST-',
      startingNumber: 1001,
      validityDays: 30,
      currentNumber: 1001
    };
  }
};

export const getInvoiceSettings = async () => {
  try {
    const settings = await fetchSettingsFromAPI();
    return settings.invoiceSettings;
  } catch (error) {
    console.warn('Using default invoice settings due to API error');
    return {
      prefix: 'INV-',
      startingNumber: 1201,
      taxRate: 18,
      discountRate: 10,
      currentNumber: 1201
    };
  }
};

export const getClosedOrderSettings = async () => {
  try {
    const settings = await fetchSettingsFromAPI();
    return settings.closedOrderSettings;
  } catch (error) {
    console.warn('Using default closed order settings due to API error');
    return {
      prefix: 'CO-',
      startingNumber: 2001,
      currentNumber: 2001
    };
  }
};

export const getCompanySettings = async () => {
  try {
    const settings = await fetchSettingsFromAPI();
    return settings.companyDetails;
  } catch (error) {
    console.warn('Using default company settings due to API error');
    return {
      companyName: 'IDEAL TRENDZ INTERIORS',
      addressLine1: '# Vertex Privilege, Villa No. 32, Nizampet Road',
      city: 'HYDERNAGAR',
      state: 'TELANGANA',
      pincode: '500072',
      contactNumber: '9014814901',
      emailId: 'idealtrendz.int@gmail.com',
      gstNumber: '36AHZPN3524A1Z9',
      panNumber: 'AHZPN3524A'
    };
  }
};
